import type { JobsiteBusinessContext, JobsiteBusinessServiceValue } from '../../types/runtime-config';
import type { ServiceItem, ServicesProps } from './services';
import type { ComponentConfig } from '@puckeditor/core';
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { ServicesFields } from "./services.fields";
import { generatedSubheading } from "../../lib/generated-subheading";
import { getWindowJobsiteConfig } from '../../types/runtime-config';
import { metadataIsOnePage } from "../../lib/one-page-metadata";
import { renderLayout } from "./services-layout";

/**
 * The business's job types as cards, as they are when the page renders: the
 * list a section with no cards of its own shows, so adding or removing a job
 * type and republishing keeps the site right without regenerating it.
 * @param {JobsiteBusinessServiceValue[] | undefined} services The business's job types.
 * @param {string[] | undefined} knownPaths Every page the site has, to link a job type's own page.
 * @returns {ServiceItem[]} One card per job type.
 */
const businessServiceCards = (
    services: JobsiteBusinessServiceValue[] | undefined,
    knownPaths: string[] | undefined,
  ): ServiceItem[] => (services ?? []).flatMap((service): ServiceItem[] => {
    if (typeof service === "string") {
      return service.trim() === "" ? [] : [{ description: "", title: service }];
    }
    const title = service.name?.trim() ?? "";
    if (title === "") {
      return [];
    }
    const slug = service.slug ?? "",
      href = slug === "" ? undefined : `/services/${slug}`;
    return [{
      description: service.description ?? "",
      // A job type links only to a page the site actually has.
      href: href !== undefined && knownPaths?.includes(href) === true ? href : undefined,
      priceCents: service.priceCents ?? null,
      title,
    }];
  }),

  /**
   * Whether this section lists the business's job types live: it is set to, and
   * the business has job types to list. Every other section shows its saved cards.
   * @param {ServicesProps["useBusinessServices"]} useBusinessServices The section's setting.
   * @param {JobsiteBusinessServiceValue[] | undefined} businessServices The business's job types.
   * @returns {boolean} True when the live list replaces the saved one.
   */
  listsBusinessServices = (
    useBusinessServices: ServicesProps["useBusinessServices"],
    businessServices: JobsiteBusinessServiceValue[] | undefined,
  ): boolean => useBusinessServices === "yes" && (businessServices?.length ?? 0) > 0;

/**
 * Fields a layout never reads. List is a fixed-width stack, so it ignores the column count;
 * Compact shows names only, so it has no columns or cards to style.
 */
const HIDDEN_BY_LAYOUT: Record<string, readonly string[]> = {
  carousel: ["columns", "variant"],
  compact: ["columns", "variant"],
  expandable: ["columns", "variant"],
  list: ["columns"],
};

const ServicesConfig: ComponentConfig<ServicesProps> = {
  defaultProps: {
    columns: "3",
    heading: "Our Professional Services",
    layout: "grid",
    services: [
      {
        description: "Professional service tailored to your needs. Quality workmanship guaranteed.",
        title: "Service One",
      },
      {
        description: "Expert solutions for your home or business. Fast, reliable service.",
        title: "Service Two",
      },
      {
        description:
          "Comprehensive service options to meet your requirements. Satisfaction guaranteed.",
        title: "Service Three",
      },
    ],
    subheading: "Quality service you can trust. Licensed professionals ready to help.",
    variant: "cards",
  },
  fields: {
    heading: {
      ...ServicesFields.heading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.services.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...ServicesFields.subheading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.services.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    layout: ServicesFields.layout,
    columns: ServicesFields.columns,
    variant: ServicesFields.variant,
    useBusinessServices: ServicesFields.useBusinessServices,
    services: ServicesFields.services,
    backgroundColor: ServicesFields.backgroundColor,
  },
  label: "Services",
  render: ({ id, puck, ...props }) => {
    // Access business data from Puck metadata OR window config (fallback)
    const puckMetadata = puck?.metadata as
        | { business?: JobsiteBusinessContext; pagePaths?: string[] }
        | undefined,
      windowConfig = getWindowJobsiteConfig(),
      business = puckMetadata?.business || windowConfig?.business,
      market = business?.primaryMarket,
      // Set of paths that were actually pre-rendered. Used to drop hrefs
      // For service cards whose dedicated /services/X page wasn't
      // Generated (plan-cap: only the top-N targeted services get pages).
      // Cards still render — the operator's full menu stays visible —
      // They just lose the link, so visitors aren't routed into a 404.
      knownPaths = puckMetadata?.pagePaths,
      filteredServices =
        knownPaths && Array.isArray(props.services)
          ? props.services.map((s) => {
            if (s.href && !knownPaths.includes(s.href)) {
              return { ...s, href: undefined };
            }
            return s;
          },
            )
          : props.services,
      // A section set to list the business's job types shows them as they are now.
      shownServices = listsBusinessServices(props.useBusinessServices, business?.services)
        ? businessServiceCards(business?.services, knownPaths)
        : filteredServices,

      // Default values if not provided in props
      resolvedProps = {
        ...props,
        heading:
          props.heading ||
          (market ? `Expert Services in ${market.name}` : "Our Professional Services"),
        services: shownServices,
        subheading:
          props.subheading ||
          generatedSubheading(
            props.variant,
            market
              ? `We provide top-rated services to homeowners in ${market.name} and surrounding areas.`
              : "We provide top-rated services to homeowners in your area.",
            metadataIsOnePage(puck?.metadata),
          ),
        variant: props.variant || "cards",
      };

    return renderLayout(id, resolvedProps);
  },
  /**
   * The list layout renders a fixed-width stack and never reads the column count. The
   * "(Grid Only)" in the label was a comment, not enforcement. A section listing the
   * business's job types live has no cards to edit here: job types are edited where
   * the business keeps them. Only a section a one-page build set up offers that choice.
   */
  resolveFields: (data, { fields, metadata }) => {
    const props = data.props as { layout?: string; useBusinessServices?: ServicesProps["useBusinessServices"] },
      business = (metadata as { business?: JobsiteBusinessContext } | undefined)?.business,
      hidden = new Set(HIDDEN_BY_LAYOUT[props.layout ?? "grid"]),
      visible = Object.fromEntries(
        Object.entries(fields as Record<string, unknown>).filter(([name]) => !hidden.has(name)),
      );

    if (props.useBusinessServices === undefined) {
      delete visible.useBusinessServices;
    }
    if (listsBusinessServices(props.useBusinessServices, business?.services)) {
      delete visible.services;
    }
    return visible as typeof fields;
  },
};

export { ServicesConfig };
